<script lang="ts">
	import fnum, { type FinancialNumber } from 'shared/number'
	import { get_kydex_color_swatch_url } from 'shared/bzns/holster/image_paths'
	import { format_money_$_with_optional_cents } from 'shared/format_currency'
	import ColorPicker from 'shared/component/ColorPicker.svelte'
	import assert from 'shared/assert'
	import map_has from 'shared/map_has'

	export let selected_color: TrexColor | null = null
	export let colors: TrexColor[]
	export let color_ref_to_additional_price: Map<number, FinancialNumber>
	export let color_image_min_size: number
	export let color_image_max_size: number
	export let style = ``
	export let promo_active: boolean
	export let promo_discount_rate: FinancialNumber
	export let promo_discount_usd: FinancialNumber
	export let id: string

	$: show_color_dropdown = colors.length > 1

	$: if (!show_color_dropdown) {
		selected_color = colors[0]
	}

	const fmt_color_name_and_price = (color: TrexColor | null) => {
		if (!color) {
			return `Select a color`
		}

		assert(map_has(color_ref_to_additional_price, color.color_ref))

		const base_color_price = fnum(color_ref_to_additional_price.get(color.color_ref))
		const fnum_price = promo_active && promo_discount_rate
			? base_color_price.times(fnum(`1`).minus(promo_discount_rate))
			: promo_active && promo_discount_usd
				? base_color_price.minus(promo_discount_usd)
				: base_color_price

		if (fnum_price.equal(fnum(`0`))) {
			return color.name
		}

		return `${ color.name } (${ format_money_$_with_optional_cents(fnum_price) })`
	}

	let dropdown_is_open = false
	const on_body_click_or_focus = () => {
		if (!check_for_focus_here.matches(`:focus-within`)) {
			dropdown_is_open = false
		}
	}

	let check_for_focus_here: Element

	let toggle_dropdown_button: HTMLButtonElement
	const toggle_dropdown = () => {
		toggle_dropdown_button.focus() // because Safari does not focus buttons when clicked
		dropdown_is_open = !dropdown_is_open
	}
</script>


<svelte:body on:click={on_body_click_or_focus} on:keyup={on_body_click_or_focus} />

<div class=main {style} bind:this={check_for_focus_here} data-dropdown_is_open={dropdown_is_open}>
	{#if show_color_dropdown}
		<button class=toggle_dropdown on:click={toggle_dropdown} bind:this={toggle_dropdown_button} {id}>
			{#if selected_color}
				<img
					src={get_kydex_color_swatch_url({
						color_file_name: selected_color.image_file_name_string,
						image_max_size: color_image_max_size,
						dpr: typeof window !== `undefined` ? window.devicePixelRatio : 2,
					})}
					alt={selected_color.name}
					class=inline_swatch
				/>
			{/if}
			<div class=color_name title={fmt_color_name_and_price(selected_color)} data-showing_placeholder={!selected_color}>
				{fmt_color_name_and_price(selected_color)}
				<div class=force_width data-extra_width_for_swatch={!selected_color}>
					{#each colors as color (color.color_ref)}
						<div>{fmt_color_name_and_price(color)}</div>
					{/each}
				</div>
			</div>
		</button>

		<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
		<div class=dropdown_box_container on:click|stopPropagation>
			<div class=dropdown_box style="max-width: {(colors.length - 0.5) * color_image_max_size}px;">
				<ColorPicker
					{colors}
					{color_ref_to_additional_price}
					{promo_active}
					{promo_discount_rate}
					bind:selected={selected_color}
					image_min_size={color_image_min_size}
					image_max_size={color_image_max_size}
				/>
			</div>
		</div>
	{:else}
		<div class=no_dropdown>
			{#if selected_color}
				<img
					src={get_kydex_color_swatch_url({
						color_file_name: selected_color.image_file_name_string,
						image_max_size: color_image_max_size,
						dpr: typeof window !== `undefined` ? window.devicePixelRatio : 2,
					})}
					alt={selected_color.name}
					class=inline_swatch
				/>
			{/if}
			<span class=color_name>{fmt_color_name_and_price(selected_color)}</span>
		</div>
	{/if}
</div>


<style>
	.main {
		--swatch-size: 1.5rem;
		--gap: 8px;
	}
	.inline_swatch {
		width: var(--swatch-size);
		height: var(--swatch-size);
		border: 1px solid #000;
	}

	.no_dropdown,
	button.toggle_dropdown {
		display: flex;
		align-items: center;
		padding: 0.5em;
		font-size: 1em;
		gap: var(--gap);
		color: var(--contrast-gray);
		font-size: 16px;
		font-weight: 400;
	}
	button.toggle_dropdown {
		background-color: var(--better-contrast-background);
		border: 1px solid var(--input-border-gray);
		text-transform: none;
		max-width: var(--dropdown-width);
		transition: none;
		letter-spacing: 0px;
	}
	button .color_name {
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
		text-align: left;
	}
	[data-showing_placeholder="true"] {
		color: #979797;
	}
	.force_width {
		visibility: hidden;
		height: 0px;
		white-space: normal;
	}
	.force_width[data-extra_width_for_swatch=true] {
		padding-right: calc(var(--swatch-size) + var(--gap));
	}
	button.toggle_dropdown::after {
		content: '⌟';
		transform: rotate(45deg);
		position: relative;
		top: -0.25em;
	}
	[data-dropdown_is_open="true"] button.toggle_dropdown::after {
		content: '✕';
		transform: rotate(0deg);
		top: 0em;
		font-size: 0.7em;
		left: -0.2em;
	}
	.dropdown_box_container {
		position: relative;
		display: none;
		z-index: 1;
	}
	[data-dropdown_is_open="true"] .dropdown_box_container {
		display: block;
	}

	.dropdown_box {
		position: absolute;
		background-color: #000;
		border: 1px solid var(--input-border-gray);
		padding: 8px;
		border-radius: 3px;
		left: var(--dropdown-left);
		width: var(--dropdown-width);
	}
</style>
